import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Button, Menu, TextField } from '@mui/material';
import FilterChip from './FilterChip';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import { capitalize, debounce } from 'lodash';
import { useToast } from '@/components/_common/Toast';
import { SnackbarKey } from 'notistack';
import PolarisIcon from '@/components/_common/PolarisIcon';

type FilterNumericRangeProps = {
  type: string;
  label?: string;
  limit?: number;
}

const FilterNumericRange = ({ type, label, limit = 3 }: FilterNumericRangeProps) => {
  const toast = useToast();
  const errorToastKey  = useRef<SnackbarKey | undefined>(undefined);
  const showError  = useRef(
    debounce((err) => {
      errorToastKey.current = toast.error(err);
    }, 1000, { maxWait: 1100 })
  );
  const dispatch = useAppDispatch();
  const filters = useAppSelector(selectDataTableFilters);
  const value = filters[type] || {};
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [state, setState] = useState<{
    min: number | undefined;
    max: number | undefined;
  }>({
    min: undefined,
    max: undefined,
  });
  const [fieldError, setFieldError] = useState<{
    min: string | undefined;
    max: string | undefined;
  }>({
    min: undefined,
    max: undefined,
  });

  useEffect(() => {
    setState({
      min: value[`${type}__gte`],
      max: value[`${type}__lte`],
    });
  }, []);

  const onApply = () => {
    dispatch(
      applyFilter({
        type,
        selections: {
          ...value,
          [`${type}__gte`]: state.min ? state.min : undefined,
          [`${type}__lte`]: state.max ? state.max : undefined,
        }
      })
    );
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => setAnchorEl(event.currentTarget);

  const handleClose = (reason: string | null) => {
    if (reason !== 'tabKeyDown') {
      setAnchorEl(null);
      setState({
        min: value[`${type}__gte`],
        max: value[`${type}__lte`],
      });
      setFieldError({
        min: undefined,
        max: undefined,
      });
    }
  }

  const handleClear = (e: any) => {
    dispatch(applyFilter({ type, selections: null }));
    setState({
      min: undefined,
      max: undefined,
    });
    e.preventDefault();
    e.stopPropagation();
  }

  const checkInputValues = (field: 'min' | 'max', input: string) => {
    showError.current.cancel();
    toast.closeSnackbar(errorToastKey.current);
    const value = Number(input);
    let error: string | undefined = undefined;
    const fieldErrorUpdate = {
      ...fieldError,
      [field]: undefined,
    };

    const checks = {
      ...state,
      [field]: value,
    };

    if (isNaN(value)) {
      error =  'You can only input numbers';
    } else if (input.length > limit) {
      error = `${capitalize(field)} cannot exceed ${limit} digits`;
    } else if (value < 0) {
      error = `${capitalize(field)} cannot be less than 0`;
    } else if (field === 'min' && checks.max && input && value >= checks.max) {
      error = `Min has to be less than Max`;
    } else if (field === 'max' && checks.min && input && value <= checks.min) {
      error = `Max has to be greater than Min`;
    } else if (field === 'min' && checks.max && input && value < checks.max) {
      fieldErrorUpdate['max'] = undefined;
    } else if (field === 'max' && checks.min && input && value > checks.min) {
      fieldErrorUpdate['min'] = undefined;
    }
    fieldErrorUpdate[field] = error;

    if (error) {
     showError.current(error);
    }

    setFieldError(fieldErrorUpdate);
  };

  const onInputChange = (source: 'min' | 'max') => (evt: React.ChangeEvent<HTMLInputElement>) => {
    checkInputValues(source, evt.target.value?.trim());

    setState({
      ...state,
      [source]: evt.target.value?.trim(),
    });
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const hasErrors = Object.values(fieldError).some((error) => !!error);
    if (!hasErrors && e.key === 'Enter') {
      onApply();
    }
  };

  const getApplied = () => {
    const start = value[`${type}__gte`];
    const end = value[`${type}__lte`];
    let applied = '';

    if (start && !end) {
      applied = `${start}+`;
    } else if (start && end) {
      applied = `${start} - ${end}`;
    } else if (end && !start) {
      applied = `<= ${end}`;
    }

    return applied ? `: ${applied}` : '';
  };

  return (
    <div style={{ marginRight: 10 }}>
      <FilterChip
        label={
          <>
            {label}<strong>{getApplied()}</strong>
          </>
        }
        isSelected={Object.keys(value).length > 0}
        onClick={handleClick as any}
        onClear={handleClear as any}
      />
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={(_evt, reason) => handleClose(reason)}
      >
        <Wrapper>
          <RowContent>
            <Input variant="filled" error={!!fieldError.min} value={state.min ?? ''} placeholder="Min" onChange={onInputChange('min')} onKeyDown={onKeyDown} type="text" />
            <Dash><PolarisIcon icon="right_arrow" /></Dash>
            <Input variant="filled" error={!!fieldError.max} value={state.max ?? ''} placeholder="Max" onChange={onInputChange('max')} onKeyDown={onKeyDown} type="text" />
          </RowContent>
          <ButtonContent>
            <Button
              disabled={(!state.max && !state.min) || !!fieldError.min || !!fieldError.max}
              onClick={onApply}
              variant="contained"
              color="primary"
            >
              Apply
            </Button>
          </ButtonContent>
        </Wrapper>
      </Menu>
    </div>
  );
};

export default FilterNumericRange;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing(1, 2)};
  gap: ${props => props.theme.spacing(0.5)};
`;

const RowContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ButtonContent = styled(RowContent)`
  margin-left: ${props => props.theme.spacing(1)};
`;

const Input = styled(TextField)<{ error?: boolean }>`
  && {
    margin: ${(props) => props.theme.spacing(2)};
    width: calc(100% - ${(props) => props.theme.spacing(4)});
    min-width: 60px;
    max-width: 60px;

    .MuiInputBase-root {
      border: 1px solid transparent;
    }

    ${props => props.error ? `
      .MuiInputBase-root {
        border: 1px solid ${props.theme.palette.error.main};
        border-radius: ${props.theme.shape.borderRadius}px;
      }
    `: null};

    &.MuiFormControl-root {
      margin: 0;
    }

    input {
      padding: ${props => props.theme.spacing(1.5, 1.5)};
    }

    & .MuiInputBase-root {
      padding-right: 0;
      font-size: 1rem;
      min-height: 35px;
    }

    & .MuiInputLabel-root {
      font-size: 1rem;
      line-height: 2rem;
    }
  }
`;

const Dash = styled.span`
  margin: 0 ${(props) => props.theme.spacing(1)};
`